import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLOR_OPTIONS = [
  "#f43f5e", "#d946ef", "#8b5cf6", "#3b82f6",
  "#0ea5e9", "#00b5cc", "#10b981", "#84cc16",
  "#eab308", "#f97316", "#145c8c", "#0b2c47",
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function ColorPicker({ value, onChange, testId }) {
  const [hex, setHex] = useState(value || "#145c8c");

  useEffect(() => { setHex(value || "#145c8c"); }, [value]);

  const handleHexChange = (raw) => {
    let v = raw.trim();
    if (v && !v.startsWith("#")) v = "#" + v;
    v = v.slice(0, 7);
    setHex(v);
    if (HEX_RE.test(v)) onChange(v.toLowerCase());
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="grid grid-cols-6 gap-2">
        {COLOR_OPTIONS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => { setHex(c); onChange(c); }}
            className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${
              value?.toLowerCase() === c ? "border-slate-900 scale-110" : "border-transparent"
            }`}
            style={{ background: c }}
            aria-label={`color ${c}`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-md border border-slate-200 shrink-0"
             style={{ background: HEX_RE.test(hex) ? hex : "#e2e8f0" }} />
        <Input
          value={hex}
          onChange={(e) => handleHexChange(e.target.value)}
          placeholder="#145c8c"
          className="font-mono text-sm uppercase tracking-wider h-9"
          maxLength={7}
          data-testid={testId ? `${testId}-hex-input` : "color-hex-input"}
        />
      </div>
      {!HEX_RE.test(hex) && (
        <p className="text-xs text-amber-600">Enter a 6-digit hex colour (e.g. #145c8c)</p>
      )}
    </div>
  );
}

export default function ColorListPage({
  title,
  singular,
  endpoint,
  defaultColor = "#145c8c",
  testidPrefix,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(defaultColor);

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setEditing(null); setName(""); setColor(defaultColor); setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it); setName(it.name); setColor(it.color); setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, { name: name.trim(), color });
        toast.success(`${singular} updated`);
      } else {
        await api.post(endpoint, { name: name.trim(), color });
        toast.success(`${singular} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try {
      await api.delete(`${endpoint}/${it.id}`);
      load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>{title}</h1>
          <p className="text-sm text-slate-500 mt-1">Colour-coded {title.toLowerCase()} are used across time entries and reports.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="text-white" style={{ background: "#145c8c" }}
                      data-testid={`${testidPrefix}-add-button`}>
                <Plus className="h-4 w-4 mr-1" /> New {singular}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? `Edit ${singular}` : `New ${singular}`}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1"
                         data-testid={`${testidPrefix}-name-input`} />
                </div>
                <div>
                  <Label>Colour</Label>
                  <div className="mt-2">
                    <ColorPicker value={color} onChange={setColor} testId={`${testidPrefix}-color-picker`} />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="text-white" style={{ background: "#145c8c" }}
                        data-testid={`${testidPrefix}-save-button`}>
                  {editing ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isAdmin && (
        <div className="mb-4 px-4 py-3 rounded-md bg-[#e8f1f8] border border-[#bcd6e6] text-sm text-[#0b2c47] flex items-start gap-2"
             data-testid={`${testidPrefix}-readonly-banner`}>
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#145c8c]" />
          <span>
            Read-only view. Contact your administrator to add, edit or remove {title.toLowerCase()}.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {items.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No {title.toLowerCase()} yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Colour</th>
                {isAdmin && <th className="text-right px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`${testidPrefix}-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{it.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="h-4 w-4 rounded-full" style={{ background: it.color }} />
                      <span className="text-xs text-slate-500 timer-font">{it.color}</span>
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(it)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
                              data-testid={`${testidPrefix}-edit-${it.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(it)} className="p-1.5 hover:bg-red-50 rounded-md text-red-500"
                              data-testid={`${testidPrefix}-delete-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}
